import { useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isEmpty from 'lodash/isEmpty'
import { useCurrentUser } from '../../../../hooks'
import { useParameterReplacement } from '../../../../hooks/useAbundanceEngineParameters'
import { useSearchMeetings, useWealthJourneyQuery } from '../../../../api/wealthJourney'
import { useAdvisorHome } from '../AdvisorHomeContext'
import { useDailyGreetingStyles } from './dailyGreetingStyles'

dayjs.extend(utc)

const getTimeOfDay = () => {
  const hour = new Date().getHours()
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

export function useDailyGreetingShared ({
  userName,
  rawOverdueStartDate,
  skipWealthJourneyMeetings = false
}) {
  const classes = useDailyGreetingStyles()
  const { scope } = useAdvisorHome()
  const { data: user, isLoading: isLoadingUser } = useCurrentUser()
  const [today, yesterday, overdueStartDate] = useParameterReplacement([
    '$today',
    '$yesterday',
    rawOverdueStartDate
  ])

  const resolvedFirstName = userName ?? user?.firstName

  const meetingsQuery = useMemo(() => ({
    filters: {
      startDate: [{ op: 'gte', value: today }],
      endDate: [{ op: 'lte', value: today }],
      ...(!isEmpty(scope?.values)
        ? { groupId: [{ op: 'in', value: scope.values }] }
        : {})
    },
    sort: [{ field: 'startDate', dir: 'asc' }],
    resultType: 'details'
  }), [today, scope])

  const { data: meetings, isLoading: isLoadingMeetings } = useSearchMeetings(
    meetingsQuery,
    {
      mapper: ({ data }) => data ?? [],
      enabled: Boolean(today) && !skipWealthJourneyMeetings
    }
  )

  const meetingsRemainingCount = useMemo(() => {
    return (meetings ?? []).filter((m) => {
      return m?.startDate && dayjs(m.startDate).isAfter(dayjs())
    }).length
  }, [meetings])

  const dueTodayQuery = useMemo(() => ({
    filters: {
      dueDate: [
        { op: 'gte', value: today },
        { op: 'lte', value: today }
      ],
      status: [{ op: 'notIn', value: ['Completed'] }],
      ...(!isEmpty(scope?.values)
        ? { groupIds: [{ op: 'in', value: scope.values }] }
        : {})
    },
    resultType: 'total'
  }), [today, scope])

  const { data: tasksDueTodayCount, isLoading: isLoadingDueToday } = useWealthJourneyQuery(
    'daily-greeting-tasks-due-today',
    dueTodayQuery,
    {
      queryName: 'searchTasks',
      mapper: ({ total } = {}) => total ?? 0,
      enabled: Boolean(today)
    }
  )

  const timeOfDay = getTimeOfDay()
  const greetingText = !isLoadingUser && resolvedFirstName
    ? `Good ${timeOfDay}, ${resolvedFirstName}`
    : `Good ${timeOfDay}`

  return {
    classes,
    scope,
    today,
    yesterday,
    overdueStartDate,
    meetingsRemainingCount,
    isLoadingMeetings,
    tasksDueTodayCount,
    isLoadingDueToday,
    greetingText
  }
}
